import React, { useState, useEffect } from 'react';
import CurrencySwitcher from './CurrencySwitcher';

const VpsList = ({ type, category, searchEnabled, buttonText, layout, initialPlans, initialCurrencyInfo }) => {
  const [plans, setPlans] = useState(initialPlans || []);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currency, setCurrency] = useState('usd');
  const [currencyInfo, setCurrencyInfo] = useState(initialCurrencyInfo || {
    inr_rate: 0,
    euro_rate: 0,
    show_currency: '1',
    trace_country: ''
  });

  const [sortField, setSortField] = useState('monthly_price');
  const [sortOrder, setSortOrder] = useState('asc');

  const [layoutMode, setLayoutMode] = useState(layout || 'list'); // 'list' or 'box'
  const [isMobile, setIsMobile] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    if (layout) {
      setLayoutMode(layout);
    }
  }, [layout]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Setup initial currency
    if (initialCurrencyInfo) {
      const stored = localStorage.getItem('osp_currency');
      const isIndiaTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Kolkata' || Intl.DateTimeFormat().resolvedOptions().timeZone === 'Asia/Calcutta';
      if (stored) {
        setCurrency(stored);
      } else if (initialCurrencyInfo.trace_country === 'India' || isIndiaTimezone) {
        setCurrency('inr');
      }
    }

    const timer = setTimeout(() => {
      setLoading(false);
    }, 3000);

    // Listen to global currency changes
    const handleCurrencyChange = (e) => {
      if (e.detail) {
        setCurrency(e.detail);
      }
    };
    window.addEventListener('ospCurrencyChange', handleCurrencyChange);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('ospCurrencyChange', handleCurrencyChange);
    };
  }, [initialCurrencyInfo]);

  // Price formatter
  const formatPrice = (usdPrice) => {
    const price = parseFloat(usdPrice);
    if (isNaN(price) || price <= 0) return '-';

    if (currency === 'inr' && currencyInfo.inr_rate) {
      return `₹${Math.round(price * currencyInfo.inr_rate)}`;
    } else if (currency === 'eur' && currencyInfo.euro_rate) {
      return `€${(price * currencyInfo.euro_rate).toFixed(2)}`;
    }
    return `$${price.toFixed(2)}`;
  };

  // Sorting handler
  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  // Helper to slugify names for CSS classes
  const slugify = (text) => {
    if (!text) return 'plan';
    return text
      .toString()
      .toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with -
      .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
      .replace(/\-\-+/g, '-')         // Replace multiple - with single -
      .replace(/^-+/, '')             // Trim - from start
      .replace(/-+$/, '');            // Trim - from end
  };

  // Filter plans based on search
  const filteredPlans = plans.filter(plan => {
    if (!searchEnabled) {
      return true;
    }
    const query = searchQuery.toLowerCase();
    return (
      plan.name.toLowerCase().includes(query) ||
      plan.location.toLowerCase().includes(query) ||
      plan.core.toLowerCase().includes(query) ||
      plan.ram.toLowerCase().includes(query) ||
      plan.harddisk.toLowerCase().includes(query) ||
      plan.bandwidth.toLowerCase().includes(query)
    );
  });

  // Sort plans
  const sortedPlans = [...filteredPlans].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    if (sortField === 'monthly_price') {
      aVal = parseFloat(a.monthly_price);
      bVal = parseFloat(b.monthly_price);
    } else if (sortField === 'offer_price') {
      aVal = parseFloat(a.best_offer?.offer_price || a.monthly_price);
      bVal = parseFloat(b.best_offer?.offer_price || b.monthly_price);
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    const isBox = isMobile || layoutMode === 'box';
    return (
      <div id="osp-sheet" className="osp-responsive-container f32">
        {isBox ? (
          <div className="osp-skeleton-cards osp-always-visible">
            {[1, 2, 3, 4].map(i => (
              <div className="osp-skeleton-card" key={i}>
                <div className="osp-skeleton-card-header">
                  <div className="osp-skeleton-avatar"></div>
                  <div className="osp-skeleton-title"></div>
                </div>
                <div className="osp-skeleton-card-body">
                  <div className="osp-skeleton-line"></div>
                  <div className="osp-skeleton-line"></div>
                  <div className="osp-skeleton-line"></div>
                  <div className="osp-skeleton-line"></div>
                </div>
                <div className="osp-skeleton-card-footer"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="osp-skeleton-table">
            <div className="osp-skeleton-table-header">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => <div className="osp-skeleton-th" key={i}></div>)}
            </div>
            <div className="osp-skeleton-table-body">
              {[1, 2, 3, 4, 5].map(tr => (
                <div className="osp-skeleton-tr" key={tr}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(td => <div className="osp-skeleton-td" key={td}></div>)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const displayAsBox = isMobile || layoutMode === 'box';

  return (
    <div id="osp-sheet" className="osp-responsive-container f32">
      {/* Controls: Search & Switcher */}
      <div className="osp-controls-bar">
        {searchEnabled && (
          <div className="osp-search-toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {showSearch ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="text"
                  className="osp-search-input"
                  placeholder="Search server location ..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  style={{ width: '200px', padding: '6px 10px', fontSize: '13px' }}
                />
                <button
                  type="button"
                  className="osp-search-toggle-btn"
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6c757d',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Close Search"
                >
                  <span className="dashicons dashicons-no-alt" style={{ fontSize: '18px', width: '18px', height: '18px' }}></span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="osp-search-toggle-btn"
                onClick={() => setShowSearch(true)}
                style={{
                  background: '#ffffff',
                  border: '1px solid #ccd0d4',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  padding: '6px 10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.15s ease-in-out',
                  height: '34px',
                  width: '34px'
                }}
                title="Search"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  style={{ width: '16px', height: '16px', display: 'block', color: '#50575e' }}
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
            )}
          </div>
        )}
        <div className="osp-controls-right" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          {!isMobile && (
            <div className="osp-layout-switcher">
              <button 
                type="button"
                className={`osp-layout-btn ${layoutMode === 'list' || layoutMode === 'table' ? 'active' : ''}`}
                onClick={() => setLayoutMode('list')}
                title="List View"
              >
                <span className="dashicons dashicons-list-view"></span>
              </button>
              <button 
                type="button"
                className={`osp-layout-btn ${layoutMode === 'box' ? 'active' : ''}`}
                onClick={() => setLayoutMode('box')}
                title="Grid View"
              >
                <span className="dashicons dashicons-grid-view"></span>
              </button>
            </div>
          )}
          <CurrencySwitcher traceIp={currencyInfo.trace_country} />
        </div>
      </div>

      {sortedPlans.length === 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', background: '#fff', border: '1px solid #dee2e6', borderRadius: '8px' }}>
          Nothing Found !
        </div>
      ) : displayAsBox ? (
        /* Box Grid Card Layout */
        <div className="osp-cards-view osp-vps-cards-grid f32">
          {sortedPlans.map((plan) => {
            const hasOffer = plan.best_offer?.savings > 1 && plan.best_offer?.offer_price > 0;
            const planSlug = slugify(plan.name);
            return (
              <div className={`osp-card-item osp-card-pid-${plan.pid} osp-card-name-${planSlug}`} key={plan.pid}>
                <div className="osp-card-row" style={{ borderBottom: '2px solid #dee2e6', paddingBottom: '12px' }}>
                  {(() => {
                    const nameParts = plan.name.split(/\s*[-–—]\s*/);
                    const name1 = nameParts[0] || '';
                    const name2 = nameParts[1] || '';
                    return (
                      <a href={plan.buy_url} className="osp-flag-link" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', lineHeight: '1.1' }}>
                        {plan.flag_class && (
                          <i className={`flag ${plan.flag_class}`} style={{ flexShrink: 0, margin: 0 }}></i>
                        )}
                        <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <strong style={{ fontWeight: '700', fontSize: '14px', lineHeight: '1.1' }}>{name1.trim()}</strong>
                          {name2 && (
                            <span style={{ fontSize: '11px', color: '#6c757d', fontWeight: 'normal', marginTop: '2px', lineHeight: '1.1' }}>
                              {name2.trim()}
                            </span>
                          )}
                        </span>
                      </a>
                    );
                  })()}
                  <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    <img src={`${window.ospFrontendData?.pluginUrl || ''}/assets/image/sm-windows.png`} alt="Windows VPS" style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                    <img src={`${window.ospFrontendData?.pluginUrl || ''}/assets/image/sm-linux.png`} alt="Linux VPS" style={{ verticalAlign: 'middle' }} />
                  </div>
                </div>
                
                <div className="osp-card-row">
                  <span className="osp-card-label">Core</span>
                  <span className="osp-card-value">{plan.core}</span>
                </div>
                <div className="osp-card-row">
                  <span className="osp-card-label">RAM</span>
                  <span className="osp-card-value">{plan.ram}</span>
                </div>
                <div className="osp-card-row">
                  <span className="osp-card-label">Harddisk</span>
                  <span className="osp-card-value">{plan.harddisk}</span>
                </div>
                <div className="osp-card-row">
                  <span className="osp-card-label">Bandwidth</span>
                  <span className="osp-card-value">{plan.bandwidth}</span>
                </div>
                <div className="osp-card-row">
                  <span className="osp-card-label">DC Location</span>
                  <span className="osp-card-value">{plan.location}</span>
                </div>
                <div className="osp-card-row">
                  <span className="osp-card-label">Monthly Price</span>
                  <span className={`osp-card-value osp-card-value-bold ${hasOffer ? 'strikethrough' : ''}`}>
                    {formatPrice(plan.monthly_price)}
                  </span>
                </div>
                
                {hasOffer && (
                  <div className="osp-card-row">
                    <span className="osp-card-label">Offer Price</span>
                    <span className="osp-card-value osp-card-value-bold" style={{ color: '#28a745' }}>
                      {formatPrice(plan.best_offer.offer_price)}/mo
                      <span className="osp-savings-badge">Save {plan.best_offer.savings}%</span>
                    </span>
                  </div>
                )}

                <a href={plan.buy_url} className="osp-btn-configure osp-card-footer-btn">
                  <i className="dashicons dashicons-admin-generic"></i> {buttonText || 'Configure'}
                </a>
              </div>
            );
          })}
        </div>
      ) : (
        /* CSS Grid row list layout (No table tag) */
        <div className="osp-grid-list osp-vps-grid f32">
          {/* Header Row */}
          <div className="osp-list-header">
            <div className={`osp-header-col sortable ${sortField === 'name' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('name')}>Name</div>
            <div className="osp-header-col">OS</div>
            <div className={`osp-header-col sortable ${sortField === 'core' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('core')}>Core</div>
            <div className={`osp-header-col sortable ${sortField === 'ram' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('ram')}>RAM</div>
            <div className={`osp-header-col sortable ${sortField === 'harddisk' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('harddisk')}>Harddisk</div>
            <div className={`osp-header-col sortable ${sortField === 'bandwidth' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('bandwidth')}>Bandwidth</div>
            <div className={`osp-header-col sortable ${sortField === 'location' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('location')}>DC Location</div>
            <div className={`osp-header-col sortable ${sortField === 'monthly_price' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('monthly_price')}>Monthly Price</div>
            <div className={`osp-header-col sortable ${sortField === 'offer_price' ? (sortOrder === 'asc' ? 'sort-asc' : 'sort-desc') : ''}`} onClick={() => handleSort('offer_price')}>Offer Price</div>
            <div className="osp-header-col">Action</div>
          </div>
          {/* Body Rows */}
          <div className="osp-list-body">
            {sortedPlans.map((plan) => {
              const hasOffer = plan.best_offer?.savings > 1 && plan.best_offer?.offer_price > 0;
              const planSlug = slugify(plan.name);
              return (
                <div className={`osp-list-row osp-row-pid-${plan.pid} osp-row-name-${planSlug}`} key={plan.pid}>
                  <div className="osp-col-name">
                    <a href={plan.buy_url} className="osp-flag-link">
                      {plan.flag_class && <i className={`flag ${plan.flag_class}`}></i>}
                      {plan.name}
                    </a>
                  </div>
                  <div className="osp-col-os">
                    <img src={`${window.ospFrontendData?.pluginUrl || ''}/assets/image/sm-windows.png`} alt="Windows VPS" style={{ marginRight: '4px' }} />
                    <img src={`${window.ospFrontendData?.pluginUrl || ''}/assets/image/sm-linux.png`} alt="Linux VPS" />
                  </div>
                  <div className="osp-col-core">{plan.core}</div>
                  <div className="osp-col-ram">{plan.ram}</div>
                  <div className="osp-col-harddisk">{plan.harddisk}</div>
                  <div className="osp-col-bandwidth">{plan.bandwidth}</div>
                  <div className="osp-col-location">{plan.location}</div>
                  <div className="osp-col-price">
                    <span className={`osp-price-monthly ${hasOffer ? 'strikethrough' : ''}`}>
                      {formatPrice(plan.monthly_price)}
                    </span>
                  </div>
                  <div className="osp-col-offer">
                    {hasOffer ? (
                      <>
                        <span className="osp-price-discounted">{formatPrice(plan.best_offer.offer_price)}</span>
                        <span className="osp-price-term">/mo</span>
                        <br />
                        <span className="osp-savings-badge">Save {plan.best_offer.savings}%</span>
                      </>
                    ) : (
                      <span className="osp-no-offer">-</span>
                    )}
                  </div>
                  <div className="osp-col-action">
                    <a href={plan.buy_url} className="osp-btn-configure">
                      <i className="dashicons dashicons-admin-generic"></i> {buttonText || 'Configure'}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default VpsList;
